// @ts-strict-ignore FIXME: progressively type methods and move them out of this file
import I18n from 'APP/I18n'
import DeviceInfo from 'APP/Helpers/DeviceInfo'
import Config from 'APP/Config'
import merge from 'lodash/merge'
import { logger } from 'APP/Lib/Datadog'

import { DIALOGUE_SYSTEM_TYPE, LEGACY_DIALOGUE_SYSTEM_TYPE } from './constants'
import type { EnrichedPost, NormalizedPost, NormalizedPostListOrError } from './types'
import type { RawPost } from 'APP/Services/Mattermost/types'

type LinkContext = {
  episode_id?: string
}

export const formatPostMessage = (post: EnrichedPost): string | undefined | null => {
  if (!post) return ''
  switch (post.actionType) {
    case 'videoCall':
      return I18n.t('ConversationScreen.videoConsultation')
    case 'carePlan':
      return I18n.t('ConversationScreen.carePlan')
    case 'createChild':
      return post.actionContext && post.actionContext.text
    case 'takeIdPhoto':
      return post.actionContext && post.actionContext.text
    default: {
      // FIXME: check the arrays' length instead, empty arrays evaluate as truthy which is not what we want here
      const hasAttachment = post.file_ids || post.filenames
      if (hasAttachment) {
        return I18n.t('ConversationScreen.snippets.attachment')
      } else {
        return post.message || ''
      }
    }
  }
}

export const featureBlockedPost = () => {
  return true
}

export const isPatientVisiblePost = (post: NormalizedPost) => {
  if (
    post &&
    post.props &&
    (post.props.patient_visible === false || post.props.member_app_visible === false)
  ) {
    return false
  }

  if (post.props && post.props.retracted) {
    return false
  }

  if (Config.SUPPORTED_CHAT_BOT.name !== 'countdown') {
    if (post && post.props && post.props.sender && post.props.sender === 'countdown') {
      return false
    }
  }

  const noType = !post.type
  const emptyType = post.type === ''
  // TODO: Remove LEGACY_DIALOGUE_SYSTEM_TYPE once it's completely deprecated
  const hasSubType =
    (post.type === LEGACY_DIALOGUE_SYSTEM_TYPE || post.type === DIALOGUE_SYSTEM_TYPE) &&
    post.props.type
  const isFeatureEnabled = featureBlockedPost(post)

  return (noType || emptyType || hasSubType) && isFeatureEnabled
}

export const isReplyPost = (post: NormalizedPost) => !!post.parent_id

export const isSelfPost = (post: RawPost) =>
  post && post.props && post.props.deviceId === DeviceInfo.getUniqueId()

export const isVideoCallPost = (post: NormalizedPost) =>
  (post && post.actionType === 'videoCall') || (post.props && post.props.type === 'VIDEO_CALL')

export const isEncounterPost = (post: RawPost) =>
  post &&
  post.props &&
  post.props.actions &&
  post.props.actions[0] &&
  post.props.actions[0].uri &&
  (post.props.actions[0].uri.indexOf('/start-encounter/') !== -1 ||
    post.props.actions[0].uri.indexOf('/encounter/') !== -1)

export const ensureParsing = (data) => {
  if (typeof data === 'object') return data
  if (typeof data === 'string') {
    try {
      return JSON.parse(data)
    } catch (e) {
      logger.error('Failed to parse message data', {}, e)
    }
  }
}

export const getQuestionFromPost = (post) =>
  ensureParsing(post && post.props && post.props.question)

export const getAnswerFromPost = (post) => ensureParsing(post && post.props && post.props.answer)

export const isAnswerPost = (post) => !!getAnswerFromPost(post)

export const isQuestionPost = (post) => !!getQuestionFromPost(post)

export const isQuestionWithResponseUi = (post) => {
  const question = getQuestionFromPost(post)
  return typeof question === 'object' && question.type === 'multiChoiceQuestion'
}

export const isAnswerFromResponseUi = (post) => {
  const answer = getAnswerFromPost(post)
  return typeof answer === 'object' && answer.question_type === 'multiChoiceQuestion'
}

export const isPostWhichHidesKeyboard = (post) =>
  isQuestionWithResponseUi(post) || isAnswerFromResponseUi(post)

// FIXME: this function should only accept NormalizedPost
// It sometimes is currently receiving a NormalizedPostListOrError object, hence the union
export const isPostWhichTriggersNotifications = (
  post: NormalizedPost | NormalizedPostListOrError
) =>
  post &&
  post.type === '' &&
  isPatientVisiblePost(post) &&
  !isVideoCallPost(post) &&
  !isSelfPost(post)

export const channelToTimeStampForNewPost = (channel) => {
  try {
    const lastPostId = channel.pendingOrder[0] || channel.order[0]
    const lastPost = channel.pendingPosts[lastPostId] || channel.posts[lastPostId]
    const lastPostAt = (lastPost && lastPost.create_at) || 0
    const timeStamp = Math.max(lastPostAt + 1, Date.now())
    return timeStamp
  } catch (_err) {
    return Date.now()
  }
}

export const isChannelInconsistent = (channel) => {
  return (
    typeof channel !== 'object' ||
    !channel ||
    !!channel.error ||
    typeof channel.channelId !== 'string' ||
    typeof channel.posts !== 'object' ||
    typeof channel.draft !== 'object' ||
    typeof channel.pendingPosts !== 'object' ||
    typeof channel.msgCount !== 'number' ||
    !(channel.order instanceof Array) ||
    !(channel.pendingOrder instanceof Array) ||
    !(channel.allMembers instanceof Array)
  )
}

const slotRegEx = /{.+}/
export const addCommandSlots = (cmd, extraSlots) => {
  try {
    const match = cmd.match(slotRegEx) || ['{}']
    const slots = JSON.parse(match[0])
    const newSlots = {
      ...slots,
      ...extraSlots,
    }
    const newCommand = (match.index ? cmd.slice(0, match.index) : cmd) + JSON.stringify(newSlots)
    return newCommand
  } catch (_e) {
    return cmd
  }
}

export const isCustomMessageType = (post) =>
  post &&
  post.props &&
  ['prompt_choice_single'].includes(post.props.type) &&
  post.props.choices &&
  post.props.choices.some((choice) => choice.link !== undefined)

export const isCalendar = (post) => {
  return (
    isCustomMessageType(post) &&
    post?.props?.utterance === 'utter_calendar_url' &&
    post?.props?.choices[0]?.link?.startsWith('data:text/calendar')
  )
}

export const isEpharmacyPrescription = (post) =>
  isCustomMessageType(post) && !!post?.props?.choices[0]?.context?.epharmacy_id

export const isCmsContent = (post) => {
  return isCustomMessageType(post) && post?.props?.choices[0]?.context?.link_type === 'cms'
}

export const isFile = (post) => {
  return Boolean(post.file_ids?.[0] || post.localFile)
}

export const isResourceLink = (post) => post && post.props && post.props.resource

export const onlyDisplayedWhenLastPost = (post) => {
  if (
    post?.props?.type === 'findAddress.v1' ||
    post?.props?.type === 'prompt_address' ||
    post?.props?.microapp?.displayed_on === 'last_message'
  ) {
    return true
  }

  return false
}

export const formatLegacyMultiSelectAnswer = (post, choices) => {
  const allChoices = post?.props?.choices
  const requestedSlot = post?.props?.requested_slot

  const answer = {
    [requestedSlot]: true,
  }

  allChoices.forEach((value) => {
    if (choices.indexOf(value) !== -1) {
      answer[value.slot] = value.true_value
    } else {
      answer[value.slot] = value.false_value
    }
  })
  return `/answer_question${JSON.stringify(answer)}`
}

/**
 * Checks if a mattermost message uses a legacy payload template
 * using `payload_template` or has a slot to set
 * and returns the appropriate payload template string.
 *
 * @param post - The post to check.
 * @returns the payload template string require to process the answer
 */

export const getPayloadTemplateFromPost = (post) => {
  const slotToSet = post?.props?.payload?.slot_to_set ?? ''

  return (
    post?.props?.payload_template ?? (slotToSet ? `/answer_question{"${slotToSet}": %VALUE%}` : '')
  )
}

/**
 * Checks if a mattermost message is an archivable system post.
 * @param post - The post to check.
 * @returns True if the post is an archivable system post, else false.
 */
export const hasIsArchivableSystemPost = (post?: Record<string, unknown>) =>
  post?.type === 'custom_dialogue_system' &&
  post?.props?.dialogue_type === 'episode_props_updated' &&
  typeof post.props.updated_props?.is_archivable === 'boolean'

/**
 * Finds the archivable system post within hidden posts.
 * @param hiddenPosts - The collection of hidden posts.
 * @returns The archivable post if found, else null.
 */
export const findArchivableSystemPost = (hiddenPosts?: Record<string, unknown>) => {
  if (!hiddenPosts) {
    return null
  }

  const posts = Object.values(hiddenPosts)
  const archivablePost = posts.findLast(hasIsArchivableSystemPost)

  if (archivablePost) {
    return archivablePost
  }

  return null
}

/**
 * Updates the episode with the archivable status from hidden posts.
 * @param hiddenPosts - The collection of hidden posts.
 * @param episode - The episode to update.
 * @returns The updated episode or null
 */
export const updateEpisodeWithArchivableStatus = (hiddenPosts = {}, episode) => {
  if (!episode) {
    return null
  }

  const archivablePost = findArchivableSystemPost(hiddenPosts)

  if (archivablePost) {
    return merge({}, episode, {
      isArchivable: archivablePost.props.updated_props.is_archivable,
    })
  }

  return null
}

export const canRedirectToConversation = (link: string, linkContext: LinkContext) => {
  return !!(link === 'conversation' && linkContext?.episode_id)
}
